import { Link } from '@mui/material';
import { t } from 'i18next';
import { PATHS } from '../../../routing/routes';
import { BlankCell } from '../../components/Cells/BlankCell';
import { FilterConfiguration } from '../../components/FilterPanel/types';
import { TypographyOverflow } from '../../components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState, PrefilledDataConfig } from '../../controllers/PaginatedTableController';
import { DefaultHeaderConfiguration, FieldConfiguration } from '../../types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../types/pagination';
import { DateUtils } from '../../utils/DateUtils';
import { UrlHelper } from '../../utils/UrlHelper';
import { KeyCertificate } from '../models/KeyCertificate';
import { KeyCertificatesService, KeyVariant } from '../services/KeyCertificatesService';

type State = PaginatedTableState & {};

type BaseProps = {
  keyId: number;
  initialData?: PaginationResult<KeyCertificate> | null;
  parentLoading?: boolean;
  withPrefill?: boolean;
};

type ReportKeyProps = BaseProps & {
  reportId: number;
  keyVariant: KeyVariant.ReportKey;
};

type OrgKeyProps = BaseProps & {
  keyVariant: KeyVariant.OrganizationKey;
};

type Props = ReportKeyProps | OrgKeyProps;

export const DEFAULT_KEY_CERTS_PAGE_SIZE = 10;

export class KeyCertificatesTable extends PaginatedTableController<KeyCertificate, Props, State> {
  private readonly certificatesService: KeyCertificatesService;

  static defaultProps = {
    initialData: null,
    parentLoading: false,
    withPrefill: false,
  };

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.certificatesService = new KeyCertificatesService();
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.certificatesService.cancelAllRequests();
  }

  protected getPrefilledConfig() {
    if (!this.props.withPrefill) {
      return super.getPrefilledConfig();
    }

    return {
      prefillActive: true,
      initialLoading: this.props.parentLoading,
      initialData: this.props.initialData,
    } as PrefilledDataConfig<KeyCertificate>;
  }

  protected getDefaultPageSize(): number {
    return DEFAULT_KEY_CERTS_PAGE_SIZE;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [];
  }

  protected getDefaultHeaderConfigurations(): DefaultHeaderConfiguration {
    return { title: t('certificates') };
  }

  getCertificatePath = (model: KeyCertificate) => {
    if (this.props.keyVariant === KeyVariant.ReportKey) {
      return UrlHelper.generatePath(PATHS.REPORT_CERTIFICATE, { certificateId: model.getId().toString(), reportId: this.props.reportId.toString() });
    }
    return UrlHelper.generatePath(PATHS.CERTIFICATE, { certificateId: model.getId().toString() });
  };

  protected getFieldConfigurations(): FieldConfiguration<KeyCertificate>[] {
    return [
      {
        fieldKey: 'id',
        label: t('id'),
        description: t('id'),
        columnProps: {
          maxWidth: 100,
        },
        render: (model, { tabIndex }) => (
          <Link
            tabIndex={tabIndex}
            href={this.getCertificatePath(model)}
            aria-label={t('a11y.organization.key.certificates.certificateIdLabel', { certId: model.getId() }) as string}
          >
            {model.getId()}
          </Link>
        ),
      },
      {
        fieldKey: 'subject',
        label: t('subject'),
        description: t('subject'),
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{model.getSubject()}</TypographyOverflow>,
      },
      {
        fieldKey: 'issuer',
        label: t('issuer'),
        description: t('issuer'),
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{model.getIssuer()}</TypographyOverflow>,
      },
      {
        fieldKey: 'digestAlgorithm',
        label: t('digestAlgorithm'),
        description: t('digestAlgorithm'),
        render: (model, { hasFocus }) =>
          model.getDigestAlgorithm() ? (
            <TypographyOverflow hasFocus={hasFocus}>{model.getDigestAlgorithm()}</TypographyOverflow>
          ) : (
            <BlankCell blankText={t('noAlgorithm')} />
          ),
      },
      {
        fieldKey: 'expiryDate',
        label: t('expiryDate'),
        description: t('expiryDate'),
        render: (model, { hasFocus }) => {
          const expiryDate = model.getExpiryDate();
          return expiryDate ? (
            <TypographyOverflow hasFocus={hasFocus}>{DateUtils.toDateString(expiryDate)}</TypographyOverflow>
          ) : (
            <BlankCell blankText={t('unknown')} />
          );
        },
      },
    ];
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<KeyCertificate>> {
    try {
      return await this.certificatesService.getKeyCertificates(this.props.keyId, this.props.keyVariant, cursorInfo, this.getFilters());
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }
}
